import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  CircularProgress,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import Layout from "../layout/layout";
import { api } from "../services/api";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import azurelogo from '../assets/azurelogo.svg';
import awssvgrep from '../assets/Amazon_Web_Services_Logo.svg';
import gcpIcon from '../assets/gcpIcon.svg';
import { useSelectedProvider } from "../services/selectedProiderContext";

 
const maskId = (id) => {
  return id ? `${id.substring(0, 3)}...${id.slice(-3)}` : "N/A";
};

function getCloudProviderValue(providerText, cloudProviders) {
  const entry = cloudProviders.find(
    (p) =>
      (p.text?.toLowerCase() === String(providerText).toLowerCase()) ||
      (p.value === providerText)
  );
  return entry ? entry.value : "";
}

const getCloudProviderLogo = (providerValue) => {
  if (!providerValue) return null;
  switch (String(providerValue)) {
    case "1": return azurelogo;
    case "2": return awssvgrep;
    case "3": return gcpIcon;
    default: return null;
  }
};

const providerHandlers = {
  aws: (selectedProject) => selectedProject.accountId,
  azure: (selectedProject) => selectedProject.subscriptionId,
  gcp: (selectedProject) => selectedProject.projectId,
};
 
const Project = () => {
  const theme = useTheme(); 
  const isDark = theme.palette.mode === 'dark';

  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [cloudProviders, setCloudProviders] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [openAddSubscriptionDialog, setOpenAddSubscriptionDialog] = useState(false);
  const [subscriptions, setSubscriptions] = useState([]);
  const userRole = sessionStorage.getItem("rolename") || sessionStorage.getItem("userRole");
  const [loading, setLoading] = useState(false);
  const { selectAccount } = useSelectedProvider();
 
  const userId = sessionStorage.getItem("userId");

  // ========== DATA FETCHING ==========

    const fetchCloudProviders = async () => {
      try {
        const response = await api.get('/Client/cloudProviders');
        setCloudProviders(response.data);
      } catch (error) {
        console.error("Error fetching cloud providers:", error);
      }
    };
 
  const fetchProjects = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/Project/getProjectDetails/${userId}`);
      setProjects(response.data);
    } catch (error) {
      toast.error(error.message || "Error fetching project details.");
    } finally {
    setLoading(false);
  }
  };
 
  useEffect(() => {
    fetchCloudProviders();
    fetchProjects();
  }, [userId]);
 
  useEffect(() => {
    const fetchSubscriptions = async () => {
      if (selectedProject?.tenantID) {
        try {
          const response = await api.get(`/Project/getsubscriptions/${selectedProject.tenantID}`);
          setSubscriptions(response.data);
        } catch (error) {
          console.error("Error fetching subscriptions:", error);
        }
      }
    };
    fetchSubscriptions();
  }, [selectedProject?.tenantID]);
 
  // ========== HANDLERS ==========

  const handleSelectProject = (projectId) => setSelectedProjectId(projectId);
 
const handleProceedClick = async () => {
    const project = projects.find(p => p.projectId === selectedProjectId);
    if (!project) return;
 
    const providerEntry = cloudProviders.find(
      p => p.text?.toLowerCase() === project.cloudProvider?.toLowerCase()
    );
    if (!providerEntry) return;

      try {
      const providerKey = project.cloudProvider.toLowerCase();
      const handler = providerHandlers[providerKey];
      const activeAccountId = handler ? handler(project) : project.accountId;
 
        const response = await api.post(`/Account/${userId}/${activeAccountId}`);
 
        if (response.status === 200 && response.data?.success) {
          selectAccount(project, providerEntry.value, activeAccountId, response.data);
          toast.success("Active account updated successfully.");
 
          const provider = project.cloudProvider.toLowerCase();
          const navigationState = { provider };
 
          if (provider === "aws") {
            navigate('/resources', { state: navigationState });
          } else if (provider === "azure") {
            navigate('/resources', { state: navigationState });
          }else if (provider === "gcp") {
            navigate('/resources', { state: navigationState });
          } else {
            toast.error("Unknown cloud provider.");
          }
        } else {
          toast.error("Failed to update active account.");
        }
      } catch (error) {
      toast.error(error.message || "Error updating active account.");
    }
  };
 
const handleEditClick = (project) => {
 const providerEntry = cloudProviders.find(
      p => p.text?.toLowerCase() === project.cloudProvider?.toLowerCase()
  );
  setSelectedProject({
    ...project,
      cloudProvider: providerEntry ? providerEntry.value : ''
  });
  setOpenEditDialog(true);
};
 
  const handleToggleProjectStatus = async (project, newStatus) => {
    setLoading(true);
    try {
      const cloudProviderValue = getCloudProviderValue(project.cloudProvider, cloudProviders);
      const payload = {
        projectName: project.projectName,
        cloudSubscriptionId: project.subscriptionId,
        cloudProvider: cloudProviderValue,
        organizationId: project.organizationId,
        tenantID: project.tenantID,
        accountId: project.accountId ? project.accountId.toString() : null,
        isActive: newStatus,
        userId: userId,
      };

      const response = await api.post(`/Project/UpdateProjectDetails/${project.projectId}`, payload);

      if (response.status === 200) {
        toast.success(`Project ${newStatus ? "enabled" : "disabled"} successfully.`);
        fetchProjects();
      }
    } catch (error) {
      toast.error(error.message || "Error updating project status.");
    } finally {
      setLoading(false);
    }
  };
 
const handleEditSave = async () => {
    if (!selectedProject) return;
    setLoading(true);
    const providerEntry = cloudProviders.find(p => p.value === selectedProject.cloudProvider);
 
    const payload = {
      projectName: selectedProject.projectName,
      cloudSubscriptionId: selectedProject.subscriptionId,
      cloudProvider: providerEntry ? providerEntry.value : null,
      organizationId: selectedProject.organizationId,
      tenantID: selectedProject.tenantID,
      accountId: selectedProject.accountId?.toString() || null,
      userId: userId
    };
 
    try {
      const response = await api.post(`/Project/UpdateProjectDetails/${selectedProject.projectId}`, payload);
      if (response.status === 200) {
        toast.success("Project updated successfully.");
        setOpenEditDialog(false);
        fetchProjects();
      }
    } catch (error) {
      toast.error(error.message || "Error updating project.");
    } finally {
      setLoading(false);
  }
};
 
  const handleAddSubscriptionSave = async () => {
    if (!selectedProject) return;
      setLoading(true);
    const providerEntry = cloudProviders.find(p => p.text === selectedProject.cloudProvider);
 
    const payload = {
        projectName: selectedProject.projectName,
        cloudSubscriptionId: selectedProject.subscriptionId,
      cloudProvider: providerEntry?.value || null,
        organizationId: selectedProject.organizationId,
        tenantID: selectedProject.tenantID,
      accountId: selectedProject.accountId?.toString() || null,
      };
 
      try {
      const response = await api.get(`/Project/${selectedProject.projectId}`, payload);
        if (response.status === 200) {
          toast.success("Subscription added successfully.");
          setOpenAddSubscriptionDialog(false);
          fetchProjects();
        }
      } catch (error) {
      toast.error(error.message || "Error adding subscription.");
  } finally {
    setLoading(false);
  }
};
 
  return (
    <Layout pageType="sideBar">
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                   <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ flex: 1, pl: theme.spacing(2)}}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: "center", p: 1 }}>
            <Typography variant="h6">Select Provider</Typography>
          
              <Button variant="contained" onClick={() => navigate("/add_new_tenant")} sx={{ mb: 1 }}>
              Add Provider
            </Button>
          
        </Box>
        <Divider />
 
 
 
        <Box sx={{
          // height: 'calc(100vh - 190px)',
           minHeight: 400, overflow: 'auto', display: 'flex', flexWrap: 'wrap', mt: theme.spacing(2), justifyContent: 'flex-start' }}>
 
 
 
    {projects?.map((project) => {
              const providerObj = cloudProviders.find(p => 
                p.value === project.cloudProvider || p.text?.toLowerCase() === project.cloudProvider?.toLowerCase()
              );
              const projectLogo = getCloudProviderLogo(providerObj?.value);
              const isProjectAWS = providerObj?.text === "AWS";
              const isSubscriptionMissing = project.cloudProvider && !isProjectAWS && (!project.subscriptionId || project.subscriptionId === "N/A");
 
  return (
    <Box key={project.projectId} sx={{ position: 'relative', width:'31%', m: 1 }}>
      <Paper
        onClick={() => { if (!isSubscriptionMissing && project.isActive) {
    handleSelectProject(project.projectId);}
}}
        sx={{
          p: 2,
          display: 'flex',
          flexDirection: 'row',
          height: '200px',
          cursor: (!isSubscriptionMissing && project.isActive) ? 'pointer' : 'default',
          pointerEvents: (!isSubscriptionMissing && project.isActive) ? 'auto' : 'none',
          boxShadow: isDark ? "0 4px 12px rgba(0,0,0,0.5)" : 3, 
          borderColor: selectedProjectId === project.projectId ? 'primary.main' : (isDark ? 'divider' : 'transparent'),
          borderWidth: 2,
          borderStyle: 'solid',
          bgcolor: 'background.paper', 
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          opacity: 0.8,
          position: 'relative',
          borderRadius: 2,
          '&:hover .action-icons': {
            visibility: 'visible',
          },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ mb: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Project Name:</Typography>
              <Typography variant="body2">{project.projectName}</Typography>
            </Box>
            {project.cloudProvider?.toLowerCase() === "aws" ? (
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Account ID:</Typography>
                <Typography variant="body2">{maskId(project.accountId)}</Typography>
              </Box>
            ) : (
              <>
                <Box sx={{ mb: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Subscription ID:</Typography>
                  <Typography variant="body2">{maskId(project.subscriptionId)}</Typography>
                </Box>
                <Box sx={{ mb: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Tenant ID:</Typography>
                  <Typography variant="body2">{maskId(project.tenantID)}</Typography>
                </Box>
              </>
            )}
          </Box>
          <Box>
              {projectLogo && <img src={projectLogo} alt={`${project.cloudProvider} Logo`} style={{ width: '80px' }} />}
          </Box>
        </Box>
 
                    <Box className="action-icons" sx={{ visibility: 'hidden', position: 'absolute', top: 8, right: 8, display: 'flex', gap: 1 }}>
      
                          <Button size="small" variant="outlined" color={project.isActive ? "error" : "success"} onClick={(e) => { e.stopPropagation(); handleToggleProjectStatus(project, !project.isActive); }}>
        {project.isActive ? "Disable" : "Enable"}
                          </Button>
                          <Button size="small" variant="outlined" disabled={!project.isActive} onClick={(e) => { e.stopPropagation(); handleEditClick(project); }}>
        Edit
                          </Button>
    
        </Box>
      </Paper>
 
                  {!project.isActive && (
                    <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', bgcolor: 'rgba(0,0,0,0.7)', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', zIndex: 2, borderRadius: 2 }}>
                      <Typography sx={{ color: '#fff', mb: 1, fontWeight: 'bold' }}>Project Disabled</Typography>
                       <Button variant="contained" size="small" onClick={() => handleToggleProjectStatus(project, true)}>Enable</Button>
  </Box>
)}
 
                  {isSubscriptionMissing && project.isActive && (
                    <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', bgcolor: 'rgba(0,0,0,0.7)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1, borderRadius: 2 }}>
                      <Button variant="contained" onClick={() => { setSelectedProject(project); setOpenAddSubscriptionDialog(true); }}>Add Subscription</Button>
        </Box>
      )}
    </Box>
  );
})}
 </Box>
 
        <Box sx={{ display: 'flex', justifyContent: "flex-end", p: 1 }}>
            
        </Box>
      </Box>
                )}

      {/* Edit Dialog */}
  <Dialog 
    open={openEditDialog} 
    onClose={() => setOpenEditDialog(false)} 
    maxWidth="md" 
    fullWidth
    PaperProps={{ sx: { bgcolor: 'background.paper' } }}
  >
        <DialogTitle sx={{ color: 'text.primary' }}>Edit Project</DialogTitle>
        <DialogContent>
          <TextField label="Project Name" value={selectedProject?.projectName || ""} fullWidth margin="normal" onChange={(e) => setSelectedProject({ ...selectedProject, projectName: e.target.value })} />
    <FormControl fullWidth margin="normal">
      <InputLabel>Cloud Provider</InputLabel>
            <Select value={selectedProject?.cloudProvider || ""} label="Cloud Provider" onChange={(e) => setSelectedProject({ ...selectedProject, cloudProvider: e.target.value })}>
              {cloudProviders.map((provider) => (
          <MenuItem key={provider.value} value={provider.value}>{provider.text}</MenuItem>
        ))}
      </Select>
    </FormControl>
  </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditDialog(false)}>Cancel</Button>
          <Button onClick={handleEditSave} variant="contained" color="success">Save</Button>
  </DialogActions>
</Dialog>
 
      {/* Add Subscription Dialog */}
      <Dialog 
        open={openAddSubscriptionDialog} 
        onClose={() => setOpenAddSubscriptionDialog(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{ sx: { bgcolor: 'background.paper' } }} 
      >
        <DialogTitle sx={{ color: 'text.primary' }}>Add Subscription</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel>Subscription Key</InputLabel>
            <Select value={selectedProject?.subscriptionId || ""} label="Subscription Key" onChange={(e) => setSelectedProject({ ...selectedProject, subscriptionId: e.target.value })}>
              {subscriptions.map((sub) => (
                <MenuItem key={sub.subscriptionId} value={sub.subscriptionId}>{sub.subscriptionId}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddSubscriptionDialog(false)}>Cancel</Button>
          <Button onClick={handleAddSubscriptionSave} variant="contained" color="success">Save</Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
};
 
export default Project;